// SketchModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { useBase } from 'context/BaseContext';
import { useSketch } from 'context/SketchContext';
import { useUser } from 'context/UserContext';
import useActorCapabilities from 'hooks/useActorCapabilities';
import { useIsMobile } from 'hooks/useDeviceDetect';
import useBaseAccess from 'hooks/useBaseAccess';
import {
  commitProvisional,
  discardIfProvisional,
  discardProvisional,
  getProvisionalSession,
  isProvisional,
} from 'node/provisional/provisionalNodeSession';
import { saveNodeCover } from 'services/nodes/NodeCoverService';
import { getNode } from 'services/nodes/NodeReadService';
import { moveNodeToTrash, updateNode } from 'services/nodes/NodeWriteService';
import {
  hydrateSketchBinaryFiles,
  persistSketchBinaryFiles,
} from 'services/storage/SketchUploadService';
import { canUser, PermissionAction } from 'utilities/NodeSecurity';
import {
  createEmptySketchScene,
  isSketchSceneEmpty,
  normalizeSketchScene,
  pickSketchCamera,
  serializeSketchScene,
} from 'utilities/sketchScene';

import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import Modal from 'components/modal/Modal';
import SketchCanvas from 'nodetypes/sketch/SketchCanvas';

import './SketchModal.css';

const TOOLS = Object.freeze([
  { key: 'freedraw', iconKey: 'draw', label: 'Draw' },
  { key: 'eraser', iconKey: 'ink_eraser', label: 'Eraser' },
  { key: 'selection', iconKey: 'near_me', label: 'Select' },
  { key: 'rectangle', iconKey: 'crop_square', label: 'Rectangle' },
]);

function dispatchEditorShortcut(key, { shift = false } = {}) {
  const isMac = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
  document.dispatchEvent(new KeyboardEvent('keydown', {
    bubbles: true,
    cancelable: true,
    code: `Key${String(key || '').toUpperCase()}`,
    ctrlKey: !isMac,
    key,
    metaKey: isMac,
    shiftKey: shift,
  }));
}

/**
 * SketchModal
 *
 * Theme-aware sketch editor. Saves scene + thumbnail on Done or close-with-content.
 *
 * @returns {ReactNode}
 */
const SketchModal = () => {
  const { closeSketch, isSketchOpen, sketchNode } = useSketch();
  const { activeBase } = useBase();
  const { user } = useUser();
  const { canEdit } = useActorCapabilities();
  const { can, isRoleLoading } = useBaseAccess(activeBase);
  const isMobile = useIsMobile();

  const apiRef = useRef(null);
  const [liveNode, setLiveNode] = useState(null);
  const [title, setTitle] = useState('');
  const [initialData, setInitialData] = useState(null);
  const [activeTool, setActiveTool] = useState('freedraw');
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const node = liveNode || sketchNode;
  const canWrite = Boolean(canEdit) && canUser(PermissionAction.EDIT, node, user);
  const canDelete = Boolean(node && user)
    && (node?.userKey === user?.key || (!isRoleLoading && can('node.deleteAny')));
  const isNewSketch = Boolean(node && isProvisional(node));

  const modalWidth = isMobile ? '100%' : 'max(1100px, 96vw)';
  const modalHeight = isMobile ? '100dvh' : 'max(820px, 96vh)';

  useEffect(() => {
    if (!isSketchOpen || !sketchNode?.key) {
      setLiveNode(null);
      setInitialData(null);
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    setReady(false);
    apiRef.current = null;

    const load = async () => {
      try {
        const node = await getNode(sketchNode.key) || sketchNode;
        if (cancelled) return;
        setLiveNode(node);
        setTitle(String(node?.title || '').trim() || 'New Sketch');
        const scene = normalizeSketchScene(node?.scene);
        const files = await hydrateSketchBinaryFiles(scene.files);
        if (cancelled) return;
        setInitialData({
          appState: scene.appState,
          elements: scene.elements,
          files,
        });
        setReady(true);
      } catch (error) {
        console.error('[SketchModal] failed to load sketch', error);
        if (cancelled) return;
        setLiveNode(sketchNode);
        setTitle(String(sketchNode?.title || '').trim() || 'New Sketch');
        setInitialData(createEmptySketchScene());
        setReady(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isSketchOpen, sketchNode]);

  const handleApi = useCallback((api) => {
    apiRef.current = api;
    if (api && canWrite) {
      api.setActiveTool?.({ type: 'freedraw' });
    }
  }, [canWrite]);

  /**
   * handleSelectTool
   *
   * @param {string} toolKey
   */
  const handleSelectTool = useCallback((toolKey) => {
    setActiveTool(toolKey);
    apiRef.current?.setActiveTool?.({ type: toolKey });
  }, []);

  /**
   * persistSketch
   *
   * @returns {Promise<boolean>}
   */
  const persistSketch = useCallback(async () => {
    const node = liveNode || sketchNode;
    const api = apiRef.current;
    if (!node?.key || !api || !canWrite) return false;

    const elements = typeof api.getSceneElements === 'function'
      ? api.getSceneElements()
      : [];
    if (isSketchSceneEmpty(elements) && isProvisional(node)) {
      await discardIfProvisional(node.key, node);
      return true;
    }

    setSaving(true);
    try {
      const files = typeof api.getFiles === 'function' ? api.getFiles() : {};
      const appState = typeof api.getAppState === 'function' ? api.getAppState() : {};
      const fileMeta = await persistSketchBinaryFiles({
        baseKey: node.baseKey || activeBase?.key,
        files,
        nodeKey: node.key,
      });
      const scene = serializeSketchScene({
        appState: pickSketchCamera(appState),
        elements,
        files: fileMeta,
      });

      let thumbnailStoragePath = String(node.thumbnailStoragePath || node.coverStoragePath || '').trim();
      let coverUrl = node.coverUrl || thumbnailStoragePath || null;
      let coverWidth = Number(node.width) || null;
      let coverHeight = Number(node.height) || null;
      if (!isSketchSceneEmpty(elements)) {
        try {
          const { exportToBlob } = await import('@excalidraw/excalidraw');
          const exportElements = elements.filter((element) => element?.isDeleted !== true);
          const blob = await exportToBlob({
            appState: {
              exportBackground: true,
              exportWithDarkMode: false,
              viewBackgroundColor: '#ffffff',
            },
            elements: exportElements,
            exportPadding: 24,
            files,
            maxWidthOrHeight: 1600,
            mimeType: 'image/png',
          });
          if (typeof createImageBitmap === 'function') {
            const bitmap = await createImageBitmap(blob);
            coverWidth = bitmap.width || coverWidth;
            coverHeight = bitmap.height || coverHeight;
            if (typeof bitmap.close === 'function') bitmap.close();
          }
          const uploaded = await saveNodeCover({
            baseKey: node.baseKey || activeBase?.key,
            file: new File([blob], 'sketch-thumbnail.png', { type: 'image/png' }),
            nodeKey: node.key,
          });
          thumbnailStoragePath = String(uploaded?.storagePath || '').trim();
          coverUrl = thumbnailStoragePath || coverUrl;
        } catch (coverError) {
          console.error('[SketchModal] thumbnail upload failed', coverError);
        }
      }

      const nextTitle = String(title || '').trim() || 'New Sketch';
      await updateNode(node.key, {
        coverStoragePath: thumbnailStoragePath || null,
        coverUrl: coverUrl || null,
        ...(Number.isFinite(coverWidth) && coverWidth > 0 ? { width: coverWidth } : {}),
        ...(Number.isFinite(coverHeight) && coverHeight > 0 ? { height: coverHeight } : {}),
        provisional: false,
        scene,
        thumbnailStoragePath: thumbnailStoragePath || null,
        title: nextTitle,
      }, { actor: user });
      await commitProvisional(node.key);
      return true;
    } catch (error) {
      console.error('[SketchModal] save failed', error);
      toast.error(error?.message || 'Could not save sketch.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [activeBase?.key, canWrite, liveNode, sketchNode, title, user]);

  /**
   * handleClose
   */
  const handleClose = useCallback(async () => {
    if (saving) return;
    const node = liveNode || sketchNode;
    const api = apiRef.current;
    const elements = api && typeof api.getSceneElements === 'function'
      ? api.getSceneElements()
      : [];

    if (canWrite && !isSketchSceneEmpty(elements)) {
      const saved = await persistSketch();
      if (!saved) return;
    } else if (node?.key && isProvisional(node) && isSketchSceneEmpty(elements)) {
      await discardIfProvisional(node.key, node);
    }

    closeSketch();
  }, [canWrite, closeSketch, liveNode, persistSketch, saving, sketchNode]);

  /**
   * handleDone
   */
  const handleDone = useCallback(async () => {
    if (saving) return;
    if (canWrite) {
      const saved = await persistSketch();
      if (!saved) return;
    }
    closeSketch();
  }, [canWrite, closeSketch, persistSketch, saving]);

  /**
   * handleDelete
   */
  const handleDelete = useCallback(async () => {
    const target = liveNode || sketchNode;
    if (!target?.key || saving) return;

    setSaving(true);
    try {
      const session = getProvisionalSession(target.key);
      const shouldDiscard = isProvisional(target)
        || Boolean(session && !session.committed);

      if (shouldDiscard) {
        await discardProvisional(target.key);
      } else {
        await moveNodeToTrash(target.key, {
          trashedByKey: user?.key || null,
        });
      }

      setConfirmDeleteOpen(false);
      closeSketch();
    } catch (error) {
      console.error('[SketchModal] delete failed', error);
      toast.error(error?.message || 'Could not delete this sketch.');
    } finally {
      setSaving(false);
    }
  }, [closeSketch, liveNode, saving, sketchNode, user?.key]);

  const toolButtons = useMemo(() => TOOLS.map((tool) => (
    <button
      className={`sketch-modal-tool${activeTool === tool.key ? ' is-active' : ''}`}
      disabled={!canWrite || !ready}
      key={tool.key}
      type="button"
      onClick={() => handleSelectTool(tool.key)}
    >
      {tool.label}
    </button>
  )), [activeTool, canWrite, handleSelectTool, ready]);

  if (!isSketchOpen) return null;

  return (
    <>
    <Modal
      enableClose={false}
      height={modalHeight}
      isOpen={isSketchOpen}
      maxHeight={modalHeight}
      padding="0px"
      showHeader={false}
      title="Sketch"
      width={modalWidth}
      onClose={handleClose}
    >
      <div className="sketch-modal-container">

        {/* TOOLBAR */}
        <div className="sketch-modal-toolbar">

          {/* TITLE */}
          <input
            className="sketch-modal-title"
            disabled={!canWrite}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />

          {/* TOOLS */}
          <div className="sketch-modal-tools">
            {toolButtons}
          </div>

          {/* ACTIONS */}
          <div className="sketch-modal-actions">
            <Button
              enabled={canWrite && ready}
              height="32px"
              iconKey="undo"
              label="Undo"
              onClick={() => dispatchEditorShortcut('z')}
            />
            <Button
              enabled={canWrite && ready}
              height="32px"
              iconKey="redo"
              label="Redo"
              onClick={() => dispatchEditorShortcut('z', { shift: true })}
            />
            <Button
              enabled={!saving && canWrite}
              height="32px"
              iconKey="check"
              label={saving ? 'Saving…' : 'Save'}
              onClick={handleDone}
            />
            {canDelete ? (
              <Button
                enabled={!saving && ready}
                height="32px"
                iconKey="delete"
                label="Delete"
                onClick={() => setConfirmDeleteOpen(true)}
              />
            ) : null}
            <Button
              enabled={!saving}
              height="32px"
              iconKey="close"
              label="Close"
              onClick={handleClose}
            />
          </div>
        </div>

        {/* CANVAS */}
        <div className="sketch-modal-canvas-wrapper">
          {ready ? (
            <SketchCanvas
              initialData={initialData}
              viewModeEnabled={!canWrite}
              onApi={handleApi}
            />
          ) : (
            <div className="sketch-canvas-loading">Opening sketch…</div>
          )}
        </div>
      </div>
    </Modal>
    <Confirm
      isOpen={confirmDeleteOpen}
      message={isNewSketch
        ? 'This sketch has not been saved yet. Delete it?'
        : 'This will move this sketch to the Recycle Bin. You can restore it later or permanently delete it from the Recycle Bin.'}
      setOpen={setConfirmDeleteOpen}
      title={isNewSketch ? 'Delete Sketch' : 'Move to Trash'}
      onConfirm={handleDelete}
    />
    </>
  );
};

SketchModal.displayName = 'SketchModal';

export default SketchModal;
